import { useState } from "react";
import BrandQR from "../components/BrandQR";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../components/ui/input-otp";
import { api, formatApiError, ENABLE_DEV_OTP } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft } from "lucide-react";

export default function OrganizerLogin() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const { login } = useAuth();

  const requestOtp = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-otp", { email });
      setDevOtp(data.dev_otp || "");
      setStep("otp");
      toast.success(
        data.email_sent
          ? `OTP sent to ${email}`
          : ENABLE_DEV_OTP
          ? `OTP generated for ${email}. Configure SMTP to deliver by mail.`
          : `OTP request received for ${email}. Check your email.`
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, otp });
      login(data.token, data.organizer);
      toast.success("Welcome back");
      nav("/organizer/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left panel */}
      <div className="hidden md:flex flex-col justify-between p-12 bg-black text-white">
        <Link
          to="/"
          className="flex items-center gap-2"
          data-testid="back-home-link"
        >
          <BrandQR />
          <span className="font-heading font-semibold">Growithevent</span>
        </Link>
        <div>
          <p className="overline text-zinc-400 mb-6">Organizer Access</p>
          <h1 className="font-heading text-5xl font-semibold tracking-tighter leading-tight">
            Run the door
            <br />
            like a pro.
          </h1>
          <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
            One-time passcode login. No passwords to forget, no reset flows.
            Just type your email.
          </p>
        </div>
        <p className="text-xs text-zinc-500">Swiss · High-contrast · MVP</p>
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-sm">
          <Link
            to="/"
            className="md:hidden inline-flex items-center gap-2 text-sm text-zinc-500 mb-8"
            data-testid="back-to-home-mobile"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>

          {step === "email" ? (
            <form onSubmit={requestOtp} data-testid="email-form">
              <p className="overline mb-4">Step 01</p>
              <h2 className="font-heading text-3xl font-semibold tracking-tight mb-2">
                Enter your email
              </h2>
              <p className="text-zinc-500 mb-8 leading-relaxed">
                We'll send a 6-digit code. No password needed.
              </p>
              <label className="text-sm font-medium text-zinc-900 mb-2 block">
                Email
              </label>
              <Input
                type="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 rounded-md border-zinc-200 focus-visible:ring-black"
                data-testid="login-email-input"
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full mt-6 h-11 bg-black text-white hover:bg-zinc-800 rounded-md"
                data-testid="send-otp-button"
              >
                {loading ? "Sending..." : "Send OTP"}
              </Button>
            </form>
          ) : (
            <div data-testid="otp-form">
              <p className="overline mb-4">Step 02</p>
              <h2 className="font-heading text-3xl font-semibold tracking-tight mb-2">
                Check your email
              </h2>
              <p className="text-zinc-500 mb-8 leading-relaxed">
                Enter the 6-digit code sent to{" "}
                <span className="text-zinc-900 font-medium">{email}</span>
              </p>

              {ENABLE_DEV_OTP && devOtp && (
                <div
                  className="mb-6 p-3 border border-amber-300 bg-amber-50 rounded-md text-sm"
                  data-testid="dev-otp-banner"
                >
                  <span className="overline text-amber-700 mr-2">Dev</span>
                  <span className="font-mono font-semibold text-amber-900">
                    {devOtp}
                  </span>
                  <span className="text-amber-800 ml-2">
                    (email delivery mocked)
                  </span>
                </div>
              )}

              <InputOTP
                maxLength={6}
                value={otp}
                onChange={setOtp}
                data-testid="otp-input"
              >
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot
                      key={i}
                      index={i}
                      className="w-11 h-11 border-zinc-200"
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>

              <Button
                onClick={verifyOtp}
                disabled={loading || otp.length !== 6}
                className="w-full mt-6 h-11 bg-black text-white hover:bg-zinc-800 rounded-md"
                data-testid="verify-otp-button"
              >
                {loading ? "Verifying..." : "Verify & Continue"}
              </Button>

              <button
                onClick={() => {
                  setStep("email");
                  setOtp("");
                  setDevOtp("");
                }}
                className="mt-4 text-sm text-zinc-500 hover:text-black transition-colors"
                data-testid="change-email-button"
              >
                ← Use a different email
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
